import {IRect, LengthUnit} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {MediaType} from '@colibrio/colibrio-reader-framework/colibrio-core-io-base';
import {IEpubPublication} from '@colibrio/colibrio-reader-framework/colibrio-core-publication-epub';
import {
    IContentLocation,
    IKeyboardEngineEvent,
    IPointerEngineEvent,
    IReaderViewAnnotationLayer,
    ITransformData,
    IVisibleContentRect,
    ReaderDocumentEventState,
    SyncMediaRangeSeekBehavior,
    TransformEasing
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {Key} from '../utils/Key.enum';
import {VanillaReader} from '../VanillaReader/VanillaReader';
import {
    IVanillaReaderAppEvent_NAVIGATION_COMPLETED,
    IVanillaReaderAppEvent_VIEW_TRANSFORM_STOPPED,
    IVanillaReaderAppEvent_VISIBLE_PAGES_CHANGED,
} from '../VanillaReaderEventBus/IVanillaReaderAppEventTypeMap';
import {VanillaReaderEventBus} from '../VanillaReaderEventBus/VanillaReaderEventBus';
import {VanillaReaderUiGuidedReadingContentMask} from './VanillaReaderUiGuidedReadingContentMask';
import {EPUBStructuralSematicsVocabulary} from './EPUBStructuralSemanticsVocabulary';
import {
    IVanillaMediaObjectData,
    IVanillaReaderGuidedReadingStepData,
    IVanillaReaderGuidedReadingTimelineData,
} from './VanillaReaderGuidedReadingModel';
import {
    VanillaReaderGuidedReadingStep,
    VanillaReaderGuidedReadingTimeline,
} from './VanillaReaderGuidedReadingTimeline';
import {VanillaReaderUI} from "../VanillaReaderUI/VanillaReaderUI";
import {VanillaReaderUiFabPanelMaskToggle} from "./VanillaReaderUiFabPanelMaskToggle";

import {
    IVanillaReaderUiColorTheme,
    IVanillaReaderViewOptions,
    VanillaReaderRendererNames
} from "../VanillaReader/VanillaReaderModel";
import {VanillaReaderUiFabGuidedPlayerToggle} from "./VanillaReaderUiFabGuidedPlayerToggle";
import {VanillaReaderAppMain} from "../VanillaReaderApp/VanillaReaderAppMain";
import {VanillaReaderUiOptionsPresetNames} from "../VanillaReaderUI/VanillaReaderUiModel";
import {VanillaReaderAppAssets} from "../VanillaReaderApp/VanillaReaderAppAssets";
import {VanillaReaderAppUiDefaults} from "../VanillaReaderUI/VanillaReaderAppUiDefaults";
import {VanillaReaderAPI} from "../VanillaReaderAPI/VanillaReaderAPI";

/**
 * # VanillaReaderGuidedReadingPlayer
 *
 * ## RESPONSIBILITIES
 *
 * This class is responsible for playing back steps as defined by a `VanillaReaderGuidedReadingTimeline` instance.
 *
 * Compared to the other classes in the Vanilla Reader it is a bit special. As this is still more of a Vanilla Reader
 * experiment, I have chosen not to "integrate" it into the VanillaReaderUI. Instead, it takes in both a VanillaReader
 * instance and a VanillaReaderUI instance so that it works more independently.
 *
 * Also, note that it does not use the `VanillaReaderEventBus` to call the `VanillaReader`. This is also to demonstrate
 * that you can use the `VanillaReader` "backend" without the using custom events.
 *
 * ## RELATED TYPES
 *
 * - VanillaReaderUI
 * - VanillaReader
 * - VanillaReaderGuidedReadingTimeline
 * - VanillaReaderGuidedReadingStep
 * - IVanillaMediaObjectData
 *
 *
 **/
export class VanillaReaderGuidedReadingPlayer {
    private _isActive: boolean = false;
    private _activeStep: VanillaReaderGuidedReadingStep | undefined;
    private _previousStep: VanillaReaderGuidedReadingStep | undefined;
    private _activeStepIndex: number = -1;
    private _playingAudioMap: Map<IVanillaMediaObjectData, HTMLAudioElement> = new Map();
    private _loadedAudioMediaObjectElementsMap: Map<IVanillaMediaObjectData, HTMLAudioElement> = new Map();
    private _timelineAudioMediaObjectMap: Map<VanillaReaderGuidedReadingStep, IVanillaMediaObjectData> = new Map();
    private _timeline: VanillaReaderGuidedReadingTimeline;
    private _guidedReadingContentMask: VanillaReaderUiGuidedReadingContentMask | undefined;
    private _currentVisiblePageRects: IRect[] | undefined;
    private _latestPointerUpEventTimeoutHandle: number | undefined = undefined;
    private _activeStepAnnotationLayer: IReaderViewAnnotationLayer | undefined;
    private _highlightActiveStep: boolean = true;
    private _vanillaReader: VanillaReader | undefined;
    private _vanillaReaderUi: VanillaReaderUI | undefined;
    private _debugMode: boolean = false;

    // These "_initial...State" state hold the application options that were active when the player was started.
    // When the stop method is called, they are used to "reset" the previous state. This is a simple, suboptimal
    // way to handle different application "reading modes" however. The application should have different
    // "options contexts" for the different modes (default, audiobook, screen reader optimised and guided reading).
    // Refactoring for a future release :)
    private _initialVanillaReaderViewOptionsState:  IVanillaReaderViewOptions | undefined;
    //private _initialNarrationState:  boolean = false;
    private _initialUseReducedMotionState:  boolean = true;
    private _initialPanZoomShouldShowExitButtonState:  boolean = true;

    buttonTogglePanelMask: VanillaReaderUiFabPanelMaskToggle;
    buttonTogglePlayer: VanillaReaderUiFabGuidedPlayerToggle;

    private constructor(private _vanillaReaderAppMain: VanillaReaderAppMain, private _timelineData: IVanillaReaderGuidedReadingTimelineData) {
        this._vanillaReader = this._vanillaReaderAppMain.getVanillaReaderInstance();
        this._vanillaReaderUi = this._vanillaReaderAppMain.getVanillaReaderUiInstance();

        if(!this._vanillaReader || !this._vanillaReaderUi) {
            throw new Error('VanillaReaderGuidedReadingPlayer.constructor(), VanillaReaderAppMain is not correctly set up.');
        }

        this._timeline = new VanillaReaderGuidedReadingTimeline(this._timelineData);

        this.buttonTogglePlayer = new VanillaReaderUiFabGuidedPlayerToggle(this._vanillaReaderUi?.vanillaReaderFabContainerElement);

        this.buttonTogglePlayer.onClick(()=>{
           if(!this._isActive) {
                this.start();
           } else {
                this.stop();
           }
        });

        this.buttonTogglePlayer.show();

        this.buttonTogglePanelMask = new VanillaReaderUiFabPanelMaskToggle(this._vanillaReaderUi?.vanillaReaderFabContainerElement);

        this.buttonTogglePanelMask.onClick(()=>{
            if(this._guidedReadingContentMask) {
                if(this._guidedReadingContentMask.isVisible) {
                    this.hideMaskLayer();
                } else {
                    this.showMaskLayer();
                }
            }
        });

        this._timeline.getStepsByMediaObjectType(MediaType.AUDIO_MPEG).forEach((step: VanillaReaderGuidedReadingStep)=>{
            let mediaObjectDataList = step.mediaObjects.filter(mediaObject => mediaObject.mediaType === MediaType.AUDIO_MPEG);
            if(mediaObjectDataList.length > 0) {
                mediaObjectDataList.forEach(mediaObjectData => this._timelineAudioMediaObjectMap.set(step,mediaObjectData))
            }
        });

        this._preloadAudioMediaObject(Array.from(this._timelineAudioMediaObjectMap.values())).catch(console.warn);
    }

    public set debugMode(inDebugMode: boolean) {
        this._debugMode = inDebugMode;
    }

    public get debugMode(): boolean {
        return this._debugMode;
    }

    public get activeStepIndex(): number {
        return this._activeStepIndex;
    }

    public get activeStep(): VanillaReaderGuidedReadingStep | undefined {
        return this._activeStep;
    }

    static create(vanillaReaderAppMain: VanillaReaderAppMain, timelineData: IVanillaReaderGuidedReadingTimelineData): VanillaReaderGuidedReadingPlayer {
        return new VanillaReaderGuidedReadingPlayer(vanillaReaderAppMain, timelineData);
    }

    start(): void {
        if(!this._vanillaReader || !this._vanillaReader.vanillaReaderView || !this._vanillaReaderUi) {
            throw new Error('VanillaReaderGuidedReadingPlayer.start(), Unable to get a reference to the VanillaReaderView instance.');
        }

        this._activeStepAnnotationLayer = this._vanillaReader?.vanillaReaderView?.colibrioReaderView.createAnnotationLayer('guidedReadingPlayerActiveStep');

        if(this._activeStepAnnotationLayer && this.debugMode) {
            this._activeStepAnnotationLayer.setDefaultAnnotationOptions({
                containerClassName: 'vanilla-reader__guided-reading-player__active-step',
                containerStyle: {
                    'border-color': VanillaReaderAppUiDefaults.readingRulerColorValues.green,
                    'border-style': 'solid',
                    'border-radius': '4px',
                    'padding': '2px',
                    'margin-top': '-2px',
                    'margin-left': '-2px',
                    'opacity': '0.5',
                },
            });

            this._activeStepAnnotationLayer.setLayerOptions({
                annotationInputEngineEventsEnabled: false,
                layerStyle: {'mix-blend-mode': 'multiply'},
            });

            this._activeStepAnnotationLayer.setVisible(true);
        }

        this._initialPanZoomShouldShowExitButtonState = this._vanillaReaderUi?.panZoomTool.shouldShowExitButton;
        this._initialUseReducedMotionState = this._vanillaReader?.vanillaReaderView.useReducedMotion;
        //this._initialNarrationState = this._vanillaReader?.vanillaMediaPlayer?.playerIsAttachedToView || false;
        if(this._vanillaReaderUi) this._vanillaReaderUi.panZoomTool.shouldShowExitButton = false;

        // Grab the current options for the VanillaReaderView so that we can reset them when we exit the guided reading mode.
        this._initialVanillaReaderViewOptionsState = this._vanillaReader?.vanillaReaderView.getCurrentVanillaReaderViewOptionsState();

        window.addEventListener('keyup', this._event_keyUp);
        VanillaReaderAPI.instance?.onVisiblePagesChanged(this._event_visiblePagesChanged);
        VanillaReaderAPI.instance?.onViewTransformStopped(this._event_viewTransformStopped)

        this.buttonTogglePanelMask.show();
        this._configureVanillaReader();

        this._isActive = true;

        this._vanillaReaderUi?.toaster.toast('Started Guided Reading Mode (Experimental).\n\nUse the UP and DOWN arrows to move through panels.\n\nPress ESC to silence narration.', true, 3000).catch();

        document.addEventListener('visibilitychange', this._event_onDocumentVisibilityChanged);

    }

    stop(paused: boolean = false): void {

        if(this._activeStepAnnotationLayer) {
            this._vanillaReader?.vanillaReaderView?.colibrioReaderView.destroyAnnotationLayer(this._activeStepAnnotationLayer);
        }

        this._stopAllAudioMediaObjectPlayback();

        this._resetVanillaReaderConfiguration();

        this._removeLocalStorageState();

        window.removeEventListener('keyup', this._event_keyUp);
        document.removeEventListener('visibilitychange' ,this._event_onDocumentVisibilityChanged);

        VanillaReaderAPI.removeEventListener('VISIBLE_PAGES_CHANGED', this._event_visiblePagesChanged);
        VanillaReaderAPI.removeEventListener('VIEW_TRANSFORM_STOPPED', this._event_viewTransformStopped);

        this.buttonTogglePanelMask.hide();
        this.hideMaskLayer();

        if(this._vanillaReaderUi) this._vanillaReaderUi.panZoomTool.shouldShowExitButton = this._initialPanZoomShouldShowExitButtonState;

        if(!paused) {
            this._isActive = false;
        }

        this._vanillaReaderUi?.toaster.toast('Ended Guided Reading Mode', true).catch();

        this.buttonTogglePlayer.active = false;

    }

    createMaskLayer(hostElement: HTMLElement): void {

        let readerViewElement = this._vanillaReader?.vanillaReaderView?.colibrioReaderView.getElement();
        if(!readerViewElement) {
            throw new Error('VanillaReaderGuidedReadingPlayer.createMaskLayer(), Could not create mask layer. No ReaderView has been created.');
        }

        this._guidedReadingContentMask = new VanillaReaderUiGuidedReadingContentMask(
            hostElement,
            readerViewElement,
            'beforeend',
            {});

        if(!this._guidedReadingContentMask) {
            throw new Error('VanillaReaderGuidedReadingPlayer.createMaskLayer(), Could not create mask layer. VanillaReaderUiGuidedReadingContentMask constructor failed.');
        }

    }

    moveAndResizeMaskLayerRect(rect: IRect, animationDuration: number = 600, clampToCenter: boolean = true): void {

        if(!this._guidedReadingContentMask) {
            return;
        }

        if(!this._currentVisiblePageRects || this._currentVisiblePageRects.length < 1) {
            console.warn('VanillaReaderGuidedReadingPlayer.moveAndMaskLayerRect(), No visible page rects have been set.')
            return;
        }

        let transformOptions = {
            animationOptions: {
                easing: TransformEasing.MATERIAL_DECELERATION_CURVE,
                animationDuration
            }
        }

        this._guidedReadingContentMask.resizeRelative(rect, this._currentVisiblePageRects[0],clampToCenter,transformOptions);

    }

    showMaskLayer(): void {
        let palette: IVanillaReaderUiColorTheme = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === 'ContrastDark')!;
        if(this._vanillaReaderUi) this._vanillaReaderUi.vanillaReaderUIElement.style.backgroundColor = palette.backgroundLight || '#000';
        this._guidedReadingContentMask?.show();
        if(this.activeStep) {
            let rect = this._getRectFromStepData(this.activeStep);
            if(rect) {
                this.moveAndResizeMaskLayerRect(rect);
            }
        }
    }

    hideMaskLayer(): void {
        let defaultPalette: IVanillaReaderUiColorTheme = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === 'Default')!;
        if(this._vanillaReaderUi) this._vanillaReaderUi.vanillaReaderUIElement.style.backgroundColor = defaultPalette.backgroundLight || '#fff';
        this._guidedReadingContentMask?.hide();
    }

    pageLocatorHasSteps(locator: string): boolean {
        return this._timelineHasStepsForPageIndex(this._getSpineIndexFromLocator(locator));
    }

    async goToIndex(index: number): Promise<VanillaReaderGuidedReadingStep> {
        if(index >= this._timeline.getSteps().length) {
            console.warn('VanillaReaderGuidedReadingTimeline.goToIndex(), index exceeds the number of available steps.');
        }

        let activeStepIndex = index;
        let step = this._timeline.getStep(activeStepIndex);
        await this._performStepAction(step);
        return step;
    }

    seekToInitialStepForLocator(locator: string): void {
        if(!this.pageLocatorHasSteps(locator)) {
            console.warn(`Page with locator ${locator} has no steps in the timeline.`);
            return;
        }

        let step = this._getInitialStepForLocator(locator);
        let stepIndex = this._getInitialStepIndexForLocator(locator);

        if(step && stepIndex >= 0) {
            this._setActiveStep(step);
            this._activeStepIndex = stepIndex;
        }

    }

    async next(): Promise<VanillaReaderGuidedReadingStep> {
        let activeStepIndex = this._activeStepIndex < this._timeline.getSteps().length - 1 ?
            this._activeStepIndex+1 : this._activeStepIndex;
        let step = this._timeline.getStep(activeStepIndex);
        await this._performStepAction(step);

        return step;
    }

    async previous(): Promise<VanillaReaderGuidedReadingStep> {
        let activeStepIndex = this._activeStepIndex <= 0 ? 0 : this._activeStepIndex-1;
        let step = this._timeline.getStep(activeStepIndex);
        await this._performStepAction(step);

        return step;
    }

    async getNearestVisibleStepByContentLocation(contentLocation: IContentLocation): Promise<VanillaReaderGuidedReadingStep | undefined> {
        let rectData = await this._vanillaReader?.vanillaReaderView?.fetchVisibleContentRectDataForContentLocation(contentLocation);

        if(!rectData || rectData.length < 1) {
            console.warn('VanillaReaderGuidedReadingPlayer.getNearestVisibleStepByContentLocation(), Did not find any intersecting Steps for content location.');
            return undefined;
        }

        return this.getNearestStepByPageLocatorAndPoint(contentLocation.getLocator().toString(), rectData[0].clientRect.x, rectData[0].clientRect.y);

    }

    //TODO: Also take into account step order?
    getNearestStepByPageLocatorAndPoint(pageLocator: string, x: number, y:number): VanillaReaderGuidedReadingStep | undefined {
        let steps: VanillaReaderGuidedReadingStep[] | undefined = this._getStepsForLocator(pageLocator);

        if(!steps) {
            return;
        }

        // Make sure it's percent values and not fractions
        x = x < 1 ? x * 100 : x;
        y = y < 1 ? y * 100 : y;

        let nearestStep: Map<VanillaReaderGuidedReadingStep, number> = new Map();

        steps.forEach((step: VanillaReaderGuidedReadingStep) => {

            let epubMediaObject = step.mediaObjects.find(media => media.mediaType === MediaType.APPLICATION_EPUB_ZIP);

            if(!epubMediaObject) {
                return;
            }
            let spatialSelector = epubMediaObject.selectors.get('xywh');
            if(!spatialSelector) {
                return;
            }

            let mediaObjectRect = this._parseSpatialMediaFragmentSelectorValue(spatialSelector);
            if(!mediaObjectRect) {
                return;
            }

            if(nearestStep.size === 0) {
                nearestStep.set(step, mediaObjectRect.width * mediaObjectRect.height);
            }

            let xIntersects = x >= mediaObjectRect.x && x <= mediaObjectRect.x + mediaObjectRect.width;
            let yIntersects = y >= mediaObjectRect.y && y <= mediaObjectRect.y + mediaObjectRect.height;

            if(xIntersects && yIntersects) {
                let currentNearestRectArea: number = Array.from(nearestStep.values())[0];
                if(currentNearestRectArea) {
                    if((mediaObjectRect.width * mediaObjectRect.height) < currentNearestRectArea) {
                        nearestStep.clear();
                        nearestStep.set(step, mediaObjectRect.width * mediaObjectRect.height);
                    }

                }
            }

        });

        return Array.from(nearestStep.keys())[0];

    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    async fetchContentLocationRangeFromVisibleRect(targetRect: IRect, targetRectUsesRelativeCoords: boolean = true, featherInPx: number = 10): Promise<IContentLocation | undefined> {
        let containedContentLocations =  await this.fetchContentLocationsFromVisibleRect(targetRect, targetRectUsesRelativeCoords, featherInPx);

        if(!containedContentLocations || containedContentLocations.length < 1) {
            return;
        }

        return containedContentLocations[0].createRangeTo(containedContentLocations[containedContentLocations.length -1]);
    }

    async fetchContentLocationsFromVisibleRect(targetRect: IRect, targetRectUsesRelativeCoords: boolean = true, featherInPx: number = 0): Promise<IContentLocation[] | undefined> {
        let viewTransformData: ITransformData | null | undefined = this._vanillaReader?.vanillaReaderView?.colibrioReaderView.getTransformManager().getActiveTransform();
        let viewScale: number = viewTransformData?.scale || 1;
        let transformedTargetRect: IRect;
        let visiblePage = this._vanillaReader?.vanillaReaderView?.colibrioReaderView.getVisiblePages()[0];
        let pageRect = visiblePage?.getBoundingClientRect();

        if (!visiblePage || !pageRect) {
            console.warn('VanillaReaderGuidedReadingPlayer.fetchContentLocationsFromVisibleRect(), Unable to get the bounding client rects for the visible page.');
            return;
        }

        if (targetRectUsesRelativeCoords) {
            transformedTargetRect = {
                height: pageRect.height * (targetRect.height / 100),
                width: pageRect.width * (targetRect.width / 100),
                x: pageRect.width * (targetRect.x / 100) + pageRect.x - featherInPx,
                y: pageRect.height * (targetRect.y / 100) + pageRect.y - featherInPx
            };
        } else {
            transformedTargetRect = {
                x: targetRect.x * viewScale,
                y: targetRect.y * viewScale,
                width: targetRect.width * viewScale,
                height: targetRect.height * viewScale,
            };
        }

        let transformedTargetRectBottom = transformedTargetRect.y + transformedTargetRect.height + (featherInPx * 2);
        let transformedTargetRectRight = transformedTargetRect.x + transformedTargetRect.width + (featherInPx * 2);

        if (this.debugMode) {
            let currentZoomRect = document.documentElement.querySelector('#zoomrect');
            currentZoomRect?.remove();

            let documentElement = document.documentElement.querySelector('#vanilla-reader__reader-view')!;
            let zoomRectElement = document.createElement('div');
            zoomRectElement.id = 'zoomrect';
            zoomRectElement.style.position = `absolute`;
            zoomRectElement.style.top = `${transformedTargetRect.y}px`;
            zoomRectElement.style.left = `${transformedTargetRect.x}px`;
            zoomRectElement.style.height = `${transformedTargetRect.height + (featherInPx * 2)}px`;
            zoomRectElement.style.width = `${transformedTargetRect.width + (featherInPx * 2)}px`;
            zoomRectElement.style.backgroundColor = 'orange';
            zoomRectElement.style.opacity = '0.5';
            zoomRectElement.style.zIndex = '3';
            documentElement.appendChild(zoomRectElement);
        }

        let containedRects: IVisibleContentRect[] | undefined = (await this._vanillaReader?.vanillaReaderView?.colibrioReaderView.fetchRectsForVisibleContent(visiblePage))?.rects.filter((rectData: IVisibleContentRect)=>{

            let isContainedWithin: boolean = false;

            let visibleContentClientRect = rectData.clientRect;

            let topIntersects: boolean = visibleContentClientRect.y <= transformedTargetRectBottom &&
                visibleContentClientRect.y >= transformedTargetRect.y;

            let bottomIntersects: boolean = visibleContentClientRect.bottom >= transformedTargetRect.y &&
                visibleContentClientRect.bottom <= transformedTargetRectBottom;

            let leftIntersects: boolean = visibleContentClientRect.left >= transformedTargetRect.x &&
                visibleContentClientRect.x <= transformedTargetRectRight;

            let rightIntersects: boolean = visibleContentClientRect.right <= transformedTargetRectRight &&
                visibleContentClientRect.right >= transformedTargetRect.x;

            isContainedWithin = topIntersects && bottomIntersects && leftIntersects && rightIntersects;

            if(this._activeStepAnnotationLayer && isContainedWithin && this.debugMode) {
                let annotation = this._activeStepAnnotationLayer?.createAnnotation(rectData.contentLocation.getLocator());
                annotation?.setOptions({
                    containerStyle: {
                        'border-left': leftIntersects ? '1px solid orange' : '1px solid transparent',
                        'border-right': rightIntersects ? '1px solid orange' : '1px solid transparent',
                        'border-top': topIntersects ? '1px solid orange' : '1px solid transparent',
                        'border-bottom': bottomIntersects ? '1px solid orange' : '1px solid transparent',
                    }
                });
            }

            return isContainedWithin;

        });

        return containedRects?.map(rect=> rect.contentLocation);

    }

    _setActiveStep(step: VanillaReaderGuidedReadingStep): void {
        this._previousStep = this.activeStep;
        this._activeStep = step;

        if(this._highlightActiveStep && this._activeStepAnnotationLayer && !this._guidedReadingContentMask?.isVisible) {
            let stepRect = this._getRectFromStepData(step);

            if(!stepRect) {
                return;
            }

            this._activeStepAnnotationLayer.destroyAllAnnotations();
            let stepAnnotation = this._activeStepAnnotationLayer.createAnnotation(step.locator);
            stepAnnotation.setOptions({
                containerStyle: {top: `${stepRect.y}%`, left: `${stepRect.x}%` ,width: `${stepRect.width}%`, height: `${stepRect.height}%`}
            });
        }
    }

    _getRectFromStepData(step: IVanillaReaderGuidedReadingStepData): IRect | undefined {
        let epubMediaObject = step.mediaObjects.find(media => media.mediaType === MediaType.APPLICATION_EPUB_ZIP);

        if(!epubMediaObject) {
            return;
        }
        let spatialSelector = epubMediaObject.selectors.get('xywh');
        if(!spatialSelector) {
            return;
        }

        return  this._parseSpatialMediaFragmentSelectorValue(spatialSelector);
    }

    _event_viewTransformStopped = (ev: CustomEvent<IVanillaReaderAppEvent_VIEW_TRANSFORM_STOPPED>): void => {

        this._currentVisiblePageRects = ev.detail.transformData?.visiblePageClientRects;

        // If the user has turned on guided reading, the current masked area should transform along with the publication
        // viewport
        if(ev.detail.transformData?.visiblePageClientRects && this._guidedReadingContentMask?.isVisible && ev.detail.transformData?.zoomTargetClientRect) {

            let animationDurationMs = this._initialVanillaReaderViewOptionsState?.useReducedMotion ? 0 : 600;
            let clampToCenter = false;

            if(ev.detail.transformData && ev.detail.transformData.transformData?.animating) {
                clampToCenter = true;
            }
            if(ev.detail.transformData?.transformData?.userGestureActive) {
                animationDurationMs = 0;
            }

            this.moveAndResizeMaskLayerRect(ev.detail.transformData.zoomTargetClientRect, animationDurationMs, clampToCenter);
        }
    }

    _event_publicationCloseIntent = ()=> {
        this.stop();
    }

    _event_visiblePagesChanged = (ev: CustomEvent<IVanillaReaderAppEvent_VISIBLE_PAGES_CHANGED>)=>{

        if(!this._isActive) {
            return;
        }

        if(ev.detail.visibleRangeData?.locator) {
            let pageLocator = ev.detail.visibleRangeData.locator.toString();
            let initialPageStep = this._getInitialStepForLocator(pageLocator);

            // If the timeline has steps for this page, and the page locator is not equal to the active step
            // locator, this means that the navigation was not made using the player's navigation methods.
            if(initialPageStep && initialPageStep.locator != this._activeStep?.locator) {
                this.seekToInitialStepForLocator(ev.detail.visibleRangeData.locator.toString());
                let visiblePageRect = this._vanillaReader?.vanillaReaderView?.getVisiblePagesBoundingClientRects()[0];
                if(visiblePageRect) {
                    this._goToAndZoom(initialPageStep).catch(console.warn);
                } else {
                    console.warn('VanillaReaderGuidedReadingPlayer.constructor: VISIBLE_PAGES_CHANGED, unable to get a rect for the visible page.');
                }
            } else {
                this._vanillaReader?.vanillaReaderView?.resetViewTransformation();
            }
        }
    }

    _event_pointerDown = (ev: IPointerEngineEvent)=>{
        ev.preventDefault();
    }

    _event_pointerUp = (ev: IPointerEngineEvent)=>{
        ev.preventDefault();

        if(this._latestPointerUpEventTimeoutHandle === undefined) {
            this._latestPointerUpEventTimeoutHandle = setTimeout(()=>{
                this._event_click(ev);
                this._latestPointerUpEventTimeoutHandle = undefined;
            }, 300);
        } else {
            clearTimeout(this._latestPointerUpEventTimeoutHandle);
            this._latestPointerUpEventTimeoutHandle = undefined;
            this._event_doubleClick(ev);
        }
    }

    _event_click = (ev: IPointerEngineEvent)=>{
        if(ev.readerDocumentEventState === ReaderDocumentEventState.PROCESSED) {
            return;
        }
        if (ev.clientX < document.documentElement.clientWidth / 2) {
            this.previous().catch(console.warn);
        } else {
            this.next().catch(console.warn);
        }

    }

    _event_doubleClick = (ev: IPointerEngineEvent)=> {

        if(this._vanillaReader?.vanillaReaderView?.activeTransformData && this._vanillaReader?.vanillaReaderView.activeTransformData.publicationViewportScale > 1.1) {
            return;
        }

        let locator = ev.readerView.getReadingPosition()?.getLocator();
        if(!locator) {
            console.warn('VanillaReaderGuidedReadingPlayer._event_doubleClick(), Unable to get a valid locator from ReaderView');
            return;
        }

        let step = this.getNearestStepByPageLocatorAndPoint(locator.toString(),ev.relativeClientX,ev.relativeClientY);

        if(!step) {
            console.warn('VanillaReaderGuidedReadingPlayer._event_doubleClick(), Unable to get a step for positions ', ev.relativeClientX, ev.relativeClientY);
            return;
        }

        this._performStepAction(step).catch();
    }

    _event_keyUp = (ev: IKeyboardEngineEvent | KeyboardEvent) => {
        let key = ev.key;

        switch (key) {
            case Key.ArrowUp:
                this.next().catch(console.warn);
                break;
            case Key.ArrowDown:
                this.previous().catch(console.warn);
                break;
            case Key.Escape:
                this._stopAllAudioMediaObjectPlayback();
                break;
        }
    }

    _engineEvent_keyUp = (ev: IKeyboardEngineEvent)=>{
        if(ev.readerDocumentEventState === ReaderDocumentEventState.PROCESSED) {
            return;
        }
        this._event_keyUp(ev);
    }


    /*
    * This method configures the VanillaReader to better support the guided reading experience.
    *
    * */
    _configureVanillaReader(): void {
        this._vanillaReader?.vanillaReaderView?.colibrioReaderView.addEngineEventListener('pointerdown', this._event_pointerDown);
        this._vanillaReader?.vanillaReaderView?.colibrioReaderView.addEngineEventListener('pointerup', this._event_pointerUp);
        this._vanillaReader?.vanillaReaderView?.colibrioReaderView.addEngineEventListener('keyup', this._engineEvent_keyUp);

        this._vanillaReader?.vanillaReaderView?.colibrioReaderView.setOptions({gestureOptions: {
                panZoom: {
                    allowSinglePointerPan: false
                }
            }});
        this._vanillaReader?.vanillaReaderView?.changeRenderer(VanillaReaderRendererNames.SWIPE_SINGLE, false);
        //this._vanillaReader?.useReducedMotion(true);

        VanillaReaderAPI.instance?.onPublicationClose(this._event_publicationCloseIntent, true)
    }

    _resetVanillaReaderConfiguration(): void {
        this._vanillaReader?.vanillaReaderView?.colibrioReaderView.removeEngineEventListener('pointerdown', this._event_pointerDown);
        this._vanillaReader?.vanillaReaderView?.colibrioReaderView.removeEngineEventListener('pointerup', this._event_pointerUp);
        this._vanillaReader?.vanillaReaderView?.colibrioReaderView.removeEngineEventListener('keyup', this._engineEvent_keyUp);
        //this._vanillaReader?.toggleMediaPlayer(this._initialNarrationState, {}).catch(console.warn);
        this._vanillaReader?.useReducedMotion(this._initialUseReducedMotionState);

        if(this._initialVanillaReaderViewOptionsState) {

            this._vanillaReader?.vanillaReaderView?.changeRenderer(this._initialVanillaReaderViewOptionsState.activeRendererTypeName || VanillaReaderRendererNames.RESPONSIVE,
                this._initialVanillaReaderViewOptionsState.ignoreAspectRatio)

            this._vanillaReader?.vanillaReaderView?.setVanillaReaderViewOptions(this._initialVanillaReaderViewOptionsState)
        } else {
            console.warn('VanillaReaderGuidedReadingPlayer._resetVanillaReaderConfiguration(), _initialVanillaReaderViewOptionsState is undefined.');
        }
    }

    _getNextStepIndex(): number {
        let nextStepIndex = this._activeStepIndex >= 0 ? this._activeStepIndex : 0;
        nextStepIndex = nextStepIndex < this._timeline.getSteps().length - 1 ?
            this._activeStepIndex + 1 : this._activeStepIndex;
        return nextStepIndex;
    }

    async _performStepAction(step: VanillaReaderGuidedReadingStep): Promise<void> {
        this._activeStepIndex = this._timeline._stepsListFlat.indexOf(step);
        this._setActiveStep(step);

        if(step.stepType !== EPUBStructuralSematicsVocabulary.COMICS_SOUND_AREA) {
            this._stopAllAudioMediaObjectPlayback();
            let gotoSucceeded = await this._goToAndZoom(step);

            if(this.activeStep) {
                let stepRect = this._getRectFromStepData(this.activeStep);
                if(stepRect) {
                    await this.fetchContentLocationRangeFromVisibleRect(stepRect, true, 10);
                }
            }

            if(this._vanillaReader?.vanillaMediaPlayer?.playerIsAttachedToView) {
                // If the stepType is a panel group we do not start the sync media player. Better to wait until the step
                // targets a specific panel.
                if(this.activeStep && this.activeStep.stepType != EPUBStructuralSematicsVocabulary.COMICS_PANEL_GROUP) {
                    let stepRect = this._getRectFromStepData(this.activeStep);
                    if(stepRect) {
                        let visibleLocationRange = await this.fetchContentLocationRangeFromVisibleRect(stepRect, true,  10);
                        if(visibleLocationRange) {
                            this._vanillaReader?.vanillaMediaPlayer.playLocatorRange(visibleLocationRange.getLocator().toJSON(), {seekBehaviour: SyncMediaRangeSeekBehavior.CLAMP_TO_RANGE}).catch(console.warn);
                        }
                    }
                }
            }

            if(!gotoSucceeded) {
                return;
            }

            let audioObject = step.mediaObjects.filter(audio => audio.mediaType.toLowerCase().includes('audio'));
            if(audioObject.length > 0) {
                audioObject.forEach((item)=>{
                    this._startAudioMediaObjectPlayback(item).catch(console.warn);
                })
            }
        } else {
            let audioObject = step.mediaObjects.filter(audio => audio.mediaType.toLowerCase().includes('audio'));
            if(audioObject.length > 0) {
                this._stopAllAudioMediaObjectPlayback();
                audioObject.forEach((item)=>{
                    this._startAudioMediaObjectPlayback(item).catch(console.warn);
                })
            }
        }

    }

    async _goToAndZoom(step: VanillaReaderGuidedReadingStep): Promise<boolean> {

        let animationDuration: number = this._initialVanillaReaderViewOptionsState?.useReducedMotion ? 0 : 600;
        const latestReadingPositionData = await this._vanillaReader?.vanillaReaderView?.getReadingPositionData();

        if(!latestReadingPositionData?.locator) {
            console.warn(`VanillaReaderGuidedReadingPlayer._goToAndZoom(), Unable to read locator from vanillaReaderView.getReadingPositionData().` );
            return false;
        }

        if(!this.pageLocatorHasSteps(latestReadingPositionData.locator)) {
            console.warn(`VanillaReaderGuidedReadingPlayer._goToAndZoom(), Document ${latestReadingPositionData} has no steps defined.` );
            return false;
        }
        const onNavigationCompleted = async (): Promise<boolean> => {
            let epubMediaObject = step.mediaObjects.find(item => item.mediaType === MediaType.APPLICATION_EPUB_ZIP);
            let spatialSelector: string | undefined = epubMediaObject?.selectors.get('xywh');

            if(spatialSelector) {
                let previousRect = this._previousStep ? this._getRectFromStep(this._previousStep) : undefined;
                let rect = this._parseSpatialMediaFragmentSelectorValue(spatialSelector);

                if(rect) {

                    if(previousRect && this._rectsAreEquivalent(rect, previousRect)) {
                        return Promise.resolve(true);
                    }

                    let domRect = new DOMRect(rect.x, rect.y, rect.width, rect.height);
                    this._vanillaReader?.zoomToPublicationViewportClientRect(domRect, LengthUnit.PERCENT, {animationOptions: {durationMs: animationDuration}});

                    return new Promise((resolve, _reject) => {
                        VanillaReaderEventBus.addEventListener("VIEW_TRANSFORM_STOPPED", ()=>{
                                resolve(true);
                        }, true);
                    });

                }
            }

            return Promise.reject();

        }

        if(!this._activeStep || (this._activeStep && !this._locatorsPointToSamePage(latestReadingPositionData.locator, step.locator))) {
            this._vanillaReader?.goTo(step.locator).catch(console.warn);

            return new Promise((resolve, _reject) => {
                // We must wait for the navigation to the new page to complete before transforming (zooming etc)
                const navigationCompleted = async (_ev: CustomEvent<IVanillaReaderAppEvent_NAVIGATION_COMPLETED>) => {
                    await onNavigationCompleted();
                    const visiblePageClientRects = this._vanillaReader?.vanillaReaderView?.getVisiblePagesBoundingClientRects();
                    if(visiblePageClientRects && visiblePageClientRects.length > 0) {
                        let epubMediaObject = step.mediaObjects.find(item => item.mediaType === MediaType.APPLICATION_EPUB_ZIP);
                        let spatialSelector: string | undefined = epubMediaObject?.selectors.get('xywh');

                        if(spatialSelector) {
                            let rect = this._parseSpatialMediaFragmentSelectorValue(spatialSelector);
                            if(rect && this._guidedReadingContentMask) {
                                this._guidedReadingContentMask.resizeRelative(rect,
                                    visiblePageClientRects[0],
                                    true,
                                    {animationOptions: {durationMs: animationDuration}});
                            }
                        }
                        resolve(true);
                    }
                };
                VanillaReaderEventBus.addEventListener('NAVIGATION_COMPLETED',
                    navigationCompleted , true);
            });

        } else {
            return onNavigationCompleted();
        }

    }

    _timelineHasStepsForPageIndex(pageIndex: number): boolean {
        return this._timeline.getSteps().some((step: VanillaReaderGuidedReadingStep) => {
            return this._getSpineIndexFromLocator(step.locator) === pageIndex;
        });
    }

    _locatorsPointToSamePage(locator1: string, locator2: string): boolean {
        return this._getSpineIndexFromLocator(locator1) === this._getSpineIndexFromLocator(locator2);
    }

    _rectsAreEquivalent(rect1: IRect, rect2: IRect): boolean {
        return (rect1.width === rect2.width && rect1.height === rect2.height) && (rect1.x === rect2.x && rect1.y === rect2.y);
    }

    _getRectFromStep(step: VanillaReaderGuidedReadingStep): IRect | undefined {
        let epubMediaObject = step.mediaObjects.find(item => item.mediaType === MediaType.APPLICATION_EPUB_ZIP);
        let spatialSelector: string | undefined = epubMediaObject?.selectors.get('xywh');
        return spatialSelector ? this._parseSpatialMediaFragmentSelectorValue(spatialSelector) : undefined;

    }

    _getSpineIndexFromLocator = (locator: string): number => {
        let cfiPrefix = '#epubcfi(/6/';
        return  parseInt(locator.slice(locator.indexOf(cfiPrefix) + cfiPrefix.length, locator.indexOf('!')));
    }

    _getStepsForLocator(locator: string): VanillaReaderGuidedReadingStep[] | undefined {
        let index = this._getSpineIndexFromLocator(locator);

        if(!index) {
            console.warn(`VanillaReaderGuidedReadingPlayer._getStepsForLocator(), unable to get index from locator "${locator}"`);
            return;
        }

        return this._timeline._stepsListFlat.filter((step: VanillaReaderGuidedReadingStep) => {
            let stepIndex = this._getSpineIndexFromLocator(step.locator);
            return index === stepIndex;
        });
    }


    _getInitialStepForLocator(locator: string): VanillaReaderGuidedReadingStep | undefined {
        let index = this._getSpineIndexFromLocator(locator);

        if(!index) {
            console.warn(`VanillaReaderGuidedReadingPlayer._getInitialStepForLocator(), unable to get index from locator "${locator}"`);
            return;
        }

        return this._timeline._stepsListFlat.find((step: VanillaReaderGuidedReadingStep) => {
            let stepIndex = this._getSpineIndexFromLocator(step.locator);
            return index === stepIndex;
        });
    }

    _getInitialStepIndexForLocator(locator: string): number {
        let index = this._getSpineIndexFromLocator(locator);
        return this._timeline._stepsListFlat.findIndex((step: VanillaReaderGuidedReadingStep) => {
            let stepIndex = this._getSpineIndexFromLocator(step.locator);
            return index === stepIndex;
        });
    }

    _parseSpatialMediaFragmentSelectorValue(selectorValue: string): IRect | undefined {
            let unitPrefixLength = selectorValue.indexOf(':') > 0 ?
            selectorValue.indexOf(':') + 1 : 0;

        let coords: number[] = selectorValue
            .slice(unitPrefixLength)
            .split(',').map((position: string)=>{
            return parseFloat(position);
        });

        return {
            x: coords[0],
            y: coords[1],
            width: coords[2],
            height: coords[3]
        }
    }

    _parseTemporalMediaFragmentSelector(selector: string): number[] | undefined{
        let unitPrefixLength = selector.indexOf(':') > 0 ? selector.indexOf(':') + 1 : 0;
        let positions: number[] = selector.slice(unitPrefixLength)
            .split(',')
            .map((position: string, index: number)=>{
            if(index === 0 && position === '') {
                return 0;
            }
            return parseInt(position);
        });

        return positions;

    }

    async _preloadAudioMediaObject(audioMediaObjects: IVanillaMediaObjectData[]): Promise<void> {
        for (const mediaObjectData of audioMediaObjects) {
            if(!this._loadedAudioMediaObjectElementsMap.has(mediaObjectData)) {
                await this._createAudioMediaObjectElement(mediaObjectData);
            }
        }
        return;
    }
    async _createAudioMediaObjectElement(mediaObjectData: IVanillaMediaObjectData): Promise<HTMLAudioElement | undefined> {

        if(this._loadedAudioMediaObjectElementsMap.size > 5) {
            this._loadedAudioMediaObjectElementsMap.clear();
            this._vanillaReader?.vanillaPublication?.revokeAllResourceBlobUrls();
        }

        if(this._loadedAudioMediaObjectElementsMap.has(mediaObjectData)) {
            console.warn('VanillaReaderGuidedReadingPlayer._preloadAudioMediaObject(), audio element has already been preloaded.', mediaObjectData);
            return;
        }
        let srcPublication = this._vanillaReader?.vanillaPublication?.getColibrioReaderPublication().getSourcePublication() as IEpubPublication;
        let opfBasePath = srcPublication.getOpfDocumentUrl().pathname.slice(0, srcPublication.getOpfDocumentUrl().pathname.lastIndexOf('/'));

        let audioUrl = mediaObjectData.url.startsWith('http') ? mediaObjectData.url :
            await this._vanillaReader?.vanillaPublication?.fetchResourceBlobUrl(`${opfBasePath}/${mediaObjectData.url}`);

        if(!audioUrl) {
            console.warn('VanillaReaderGuidedReadingPlayer._preloadAudioMediaObject(), unable to get a valid url for media object.', mediaObjectData);
            return;
        }

        let audioElement = new Audio(audioUrl);
        this._loadedAudioMediaObjectElementsMap.set(mediaObjectData, audioElement);
        return audioElement;
    }

    _stopAllAudioMediaObjectPlayback(): void {

        if(this._vanillaReader?.vanillaMediaPlayer) {
            this._vanillaReader?.vanillaMediaPlayer.pause();
        }

        Array.from(this._playingAudioMap.keys()).forEach((mediaObject: IVanillaMediaObjectData)=>{
           this._stopAudioMediaObjectPlayback(mediaObject);
        });
    }

     async _startAudioMediaObjectPlayback(mediaObjectData: IVanillaMediaObjectData): Promise<void> {
        const audioElement = this._loadedAudioMediaObjectElementsMap.has(mediaObjectData) ?
            this._loadedAudioMediaObjectElementsMap.get(mediaObjectData) :
            await this._createAudioMediaObjectElement(mediaObjectData);

         let temporalSelector: string | undefined = mediaObjectData?.selectors.get('t');

         if(!temporalSelector) {
             console.warn('VanillaReaderGuidedReadingPlayer._startAudioMediaObjectPlayback, No temporal selector found in media object uri.')
             return;
         }

         const selectorComponents: number[] | undefined = this._parseTemporalMediaFragmentSelector(temporalSelector);

        if(audioElement && selectorComponents) {
            this._playingAudioMap.set(mediaObjectData, audioElement);

            audioElement.ontimeupdate = (_)=>{
                if(audioElement.currentTime > (selectorComponents[1] || audioElement.duration)) {
                    audioElement.pause();
                }
            };

            audioElement.currentTime = selectorComponents[0];
            audioElement.play().catch(console.warn);
        } else {
            this._loadedAudioMediaObjectElementsMap.delete(mediaObjectData);
            console.warn('VanillaReaderGuidedReadingPlayer._startAudioMediaObjectPlayback(), unable to create audio element.', mediaObjectData);
        }
    }

     _stopAudioMediaObjectPlayback(mediaObjectData: IVanillaMediaObjectData): void {
        let audioElement = this._playingAudioMap.get(mediaObjectData);
        if(audioElement) {
            audioElement.pause();
            this._playingAudioMap.delete(mediaObjectData);
        } else {
            console.warn(`Could not find audio element ${mediaObjectData.url} in the playingAudioMap.`);
        }
    }

    async _getPublicationResourceBlob(resourcePath: string): Promise<Blob | undefined> {
        let vanillaPublication = this._vanillaReader?.vanillaPublication;
        if(vanillaPublication)  {
            let srcPublication = vanillaPublication.getColibrioReaderPublication().getSourcePublication() as IEpubPublication;
            let opfBasePath = srcPublication.getOpfDocumentUrl().pathname.slice(0, srcPublication.getOpfDocumentUrl().pathname.lastIndexOf('/'));
            let resourceOcfPath = `${opfBasePath}/${resourcePath}`;
            let blobUrl = await vanillaPublication?.fetchResourceBlobUrl(resourceOcfPath);

            if (!blobUrl) {
                throw new Error(`VanillaRegionBasedNavigation.getPublicationResourceBlobUrl(), Unable to create Blob URL for file "${resourcePath}".`);
            }

            return (await fetch(blobUrl)).blob();
        }
        return;
    }

    _revokePublicationResourceBlobUrl(url: string): void {
        this._vanillaReader?.vanillaPublication?.revokeResourceBlobUrl(url);
    }
    _revokeAllPublicationResourceBlobUrls(): void {
        this._vanillaReader?.vanillaPublication?.revokeAllResourceBlobUrls();
    }

    _setLocalStorageState(): void {
        this._vanillaReaderAppMain.setActiveUiPresetData({
            presetName: VanillaReaderUiOptionsPresetNames.GUIDED_READING_MODE,
            rollbackVanillaReaderViewOptionsData: this._initialVanillaReaderViewOptionsState
        })
    }

    _removeLocalStorageState(): void {
        this._vanillaReaderAppMain.clearActiveUiPresetData();
    }

    _event_onDocumentVisibilityChanged = () => {
        return;
        if (document.visibilityState === "hidden") {
            this.stop(true);
            this._setLocalStorageState();

        } else if(document.visibilityState === "visible" && this._isActive) {
            this.start();
        }
    }
}
